import { Component, inject, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { LocaleService } from "../../shared/services/locale.service";
import { faFacebookF, faGoogle } from "@fortawesome/free-brands-svg-icons";

import { passwordResetUrl } from "../../auth.config";
import { StudentService } from "../../shared/services/student.service";
import { ModalService } from "../../shared/services/modal.service";
import {
  ModelStudentprofile,
  studentprofilePlaceholder,
} from "../../model/model.studentprofile";
import { catchError } from "rxjs/operators";
import { empty } from "../../functions";
import { Observable, of } from "rxjs";
import { ModelUser } from "../../model/model.user";
import { UserService } from "../../shared/services/user.service";
import {
  alertMessages,
  AlertService,
} from "../../shared/services/alert.service";
import { AuthenticationService } from "../../shared/services/authentication.service";
import { ModalAccountEmailComponent } from "../../account/modal-account-email/modal-account-email.component";
import { StudentProfileService } from "../../api/services/student-profile.service";
import { StudentProfile } from "../../api/models/student-profile";

@Component({
  selector: "app-student-settings",
  templateUrl: "./student-settings.component.html",
  styleUrls: ["./student-settings.component.scss"],
  standalone: false,
})
export class StudentSettingsComponent implements OnInit {
  public locale = inject(LocaleService);
  supportedLanguages = this.locale.supportedLanguages();
  avatarPlaceholder = studentprofilePlaceholder.avatarPlaceholder;

  public passwordResetUrl = passwordResetUrl;

  faFacebookF = faFacebookF;
  faGoogle = faGoogle;

  public entity: ModelStudentprofile = {
    id: "",
    firstName: "",
    infix: "",
    lastName: "",
    birthDate: "",
    lookingFor: "",
    profileType: <any>"",
    avatarUri: "",
    profileStrength: "25",
    homeAddress: {
      street: "",
      number: "",
      numberSuffix: "",
      city: "",
      postalCode: "",
      country: "",
      latitude: "",
      longitude: "",
    },
    phoneNumber: "",
    allowEmailNotifications: false,
    hasParentalConsent: false,
    isSubscribedToNewsletter: false,
    hasMediaConsent: false,
    hasAcceptedGdprPolicy: false,
    hasDataUsageConsent: false,
    parentEmailAddress: "",
    interests: [],
    areaOfInterests: <any>[],
    workExperiences: <any>[],
    educations: <any>[],
    languageProficiencies: <any>[],
    foreignExperiences: <any>[],
    studentProfileCvs: <any>[],
    referenceCode: "",
    preferredLanguage: "nl",
    blacklistedCompanies: [],
  };

  /**
   * Formly form for the "Privacy / hide from companies" section.
   * This is the first formly-based part of student-settings and serves as
   * the seed for a wider migration of the page to formly.
   */
  blacklistForm = new FormGroup({});
  blacklistFields: FormlyFieldConfig[] = [
    {
      key: "blacklistedCompanies",
      type: "company-blacklist-input",
      props: {
        label: "Hide profile from companies",
        placeholder: "Search for a company",
      },
    },
  ];

  public currentAccount = <ModelUser>{
    id: "",
    emailAddress: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    allowEmailNotifications: true,
    isSubscribedToNewsletter: true,
    hasAcceptedGdprPolicy: true,
    preferredLanguage: "nl",
    // Target groups filter settings - default to showing all
    disableTargetGroupsCompanies: true,
    disableTargetGroupsJobs: true,
    disableTargetGroupsMeetDays: true,
  };

  /**
   * Constructor
   *
   * @param locale
   * @param userService
   * @param studentService
   * @param modal
   * @param alertService
   * @param authService
   */
  private readonly studentProfileService = inject(StudentProfileService);

  constructor(
    private userService: UserService,
    private studentService: StudentService,
    private modal: ModalService,
    private alertService: AlertService,
    private authService: AuthenticationService
  ) {}

  ngOnInit(): void {
    this.loadStudentprofile();

    this.userService.apiGetAccountCurrent().subscribe((result) => {
      if (!empty(result)) {
        this.currentAccount = result;
      }
    });
  }

  loadStudentprofile() {
    this.studentService
      .apiGetStudentprofileCurrent()
      .pipe(catchError(this.handleError("apiGetStudentprofileCurrent")))
      .subscribe((response) => {
        if (!empty(response)) {
          this.entity = Object.assign({}, <ModelStudentprofile>response);
          if (!empty(this.entity.birthDate))
            this.entity.birthDate = new Date(this.entity.birthDate);
        } else {
        }
      });
  }

  /**
   * Delete account
   */
  deleteAccount() {
    if (!empty(this.entity.id)) {
      // Make sure confirmation is given
      this.modal
        .Confirm(
          "Delete account",
          "Are you sure you want to delete your account?"
        )
        .then((confirm) => {
          if (confirm === true) {
            this.studentService
              .apiDeleteStudentprofile(this.entity.id)
              .pipe(
                catchError(
                  this.studentService.handleError(
                    "apiDeleteStudentprofile",
                    false
                  )
                )
              )
              .subscribe((result) => {
                if (result != false) {
                  this.authService.Logout();
                }
              });
          }
        });
    }
  }

  /**
   * Save student profile and account filter settings
   *
   * @constructor
   */
  Save() {
    if (empty(this.entity.id)) return;

    this.studentProfileService
      .updateStudentProfile({
        id: this.entity.id,
        body: this.entity as unknown as StudentProfile,
      })
      .pipe(
        catchError(
          this.studentService.handleError("apiUpdateStudentprofile", false)
        )
      )
      .subscribe((result) => {
        if (result !== false) {
          // Sync language to account before saving so a single PUT covers everything
          if (
            !empty(this.entity.preferredLanguage) &&
            typeof this.entity.preferredLanguage == "string"
          ) {
            this.currentAccount.preferredLanguage = this.entity.preferredLanguage;
          }

          // Save account filter settings + language in one PUT
          this.saveAccountFilterSettings();

          this.alertService.success(alertMessages.successDataSaved);

          if (
            !empty(this.entity.preferredLanguage) &&
            typeof this.entity.preferredLanguage == "string"
          ) {
            this.locale.setLanguage(this.entity.preferredLanguage, false);
          }
        }
      });
  }

  /**
   * Save account filter settings (target groups preferences)
   * Called from Save() to persist filter settings to the account
   */
  private saveAccountFilterSettings() {
    if (empty(this.currentAccount.id)) return;

    this.userService
      .apiUpdateAccountCurrent(this.currentAccount.id, this.currentAccount)
      .pipe(
        catchError(
          this.userService.handleError("apiUpdateAccountCurrent", false)
        )
      )
      .subscribe(); // Silent save, main Save() already shows success message
  }

  SaveAccount() {
    if (empty(this.currentAccount.id)) return;

    this.userService
      .apiUpdateAccountCurrent(this.currentAccount.id, this.currentAccount)
      .pipe(
        catchError(
          this.userService.handleError("apiUpdateAccountCurrent", false)
        )
      )
      .subscribe((result) => {
        if (result !== false) {
          this.alertService.success(alertMessages.successDataSaved);

          if (
            !empty(this.currentAccount.preferredLanguage) &&
            typeof this.currentAccount.preferredLanguage == "string"
          ) {
            this.locale.setLanguage(
              this.currentAccount.preferredLanguage,
              false
            );
          }
        }
      });
  }

  /**
   * Error handler
   *
   * @param operation
   * @param result
   */
  handleError<T>(operation: string = "", result?: T) {
    return (error: any): Observable<T> => {
      // Handle given operation
      switch (operation) {
        case "apiGetStudentprofileCurrent":
          // Do not perform any action, profile could be non-existing
          break;

        default:
          this.modal.Alert("Error", "Something went wrong, please try again");
          break;
      }

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  /**
   * Change account email address
   */
  changeAccountEmailAddress() {
    this.modal
      .Modal(ModalAccountEmailComponent, "Change email address", "")
      .then((result) => {
        if (result) {
        }
      })
      .catch((error) => {
        console.debug(error);
      });
  }
}
